"use client";
// Imports
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Container, Content, SubEvents } from "@/components";
import { Button } from "@/components/ui/button";
import Link from "next/link";
import { slugify } from "@/lib/helpers";
import {
  dayUntil,
  formatOrdinalDateEnAU,
  getTiming,
  getAmount,
} from "@/lib/helpersTwo";
import { canEnterCompetition } from "@/lib/competitionEntries";
import { useState, useEffect } from "react";

import useStore from "@/lib/state";

// Breadcrumbs
export function TabsSection({ page, showDates, mapIsActive }) {
  const [activeTab, setActiveTab] = useState();
  const { selectedDay } = useStore();

  useEffect(() => {
    const requestedTab = new URL(window.location.href).searchParams.get("tab");
    if (requestedTab) {
      setActiveTab(requestedTab);
    }
  }, []);

  useEffect(() => {
    // set search params
    if (activeTab) {
      const url = new URL(window.location);
      url.searchParams.set("tab", activeTab);
      window.history.replaceState({}, "", url);
    }
  }, [activeTab]);

  const showEnter = canEnterCompetition(
    page?.entries_open,
    page?.entries_close,
    page?.late_entries_close
  );
  // Render
  return (
    <div
      className="py-10 animate-section-in"
      style={{ "--anim-delay": "400ms" }}
    >
      <Container>
        <Tabs
          onValueChange={(value) => setActiveTab(value)}
          value={activeTab ? activeTab : "competitor-info"}
        >
          <TabsContainer className="shadow-inner-scroll">
            {/* <div className="shadow-inner-scroll-element"></div> */}
            <div className="flex col-span-full items-start w-full lg:col-span-2">
              <TabsList className="overflow-x-auto p-1">
                <TabsTrigger value="competitor-info">
                  Competitor Info
                </TabsTrigger>
                {page?.judging_content?.length > 0 ||
                page?.events?.length > 0 ? (
                  <TabsTrigger value="judging-display">At the Show</TabsTrigger>
                ) : null}
                {page?.sponsors_content?.length ? (
                  <TabsTrigger value="sponsors">Sponsors</TabsTrigger>
                ) : null}
                {page?.tabs?.length > 0 &&
                  page?.tabs.map((tab, index) => (
                    <TabsTrigger key={index} value={slugify(tab?.tab_title)}>
                      {tab.tab_title}
                    </TabsTrigger>
                  ))}
              </TabsList>
            </div>
          </TabsContainer>

          <TabsContent value="competitor-info">
            <TabsContainer content={page?.content}>
              <LeftContent>
                <Content content={page?.content} />
                {/* {showEnter && (
                  <div>
                    <Button variant="outline">
                      <Link target="_blank" href={page?.entries_link ?? "#"}>
                        Enter Now
                      </Link>
                    </Button>
                  </div>
                )} */}
                {page?.requires_membership && (
                  <div className="pt-5 mt-5 border-t lg:mt-10 border-foreground">
                    <p className="pb-4 level-4">
                      *This competition requires a Membership
                    </p>
                    <Button variant="outline">
                      <Link target="_blank" href="/membership">
                        Register for Membership
                      </Link>
                    </Button>
                  </div>
                )}
              </LeftContent>
              <RightContent>
                <EntryDates
                  open={page?.entries_open}
                  close={page?.entries_close}
                  lateClose={page?.late_entries_close}
                  entriesLink={page?.entries_link}
                  showEnter={showEnter}
                />
                <Content
                  content={page?.right_competitor_content}
                  centerButtons
                />
              </RightContent>
            </TabsContainer>
          </TabsContent>
          {/* TODO: Future Release */}
          <TabsContent value="judging-display">
            <TabsContainer>
              <LeftContent>
                <Content content={page?.judging_content} />
              </LeftContent>
              {page.right_judging_content?.length ? (
                <RightContent>
                  <Content
                    content={page?.right_judging_content}
                    centerButtons
                  />
                </RightContent>
              ) : null}
            </TabsContainer>
            <div className="">
              <SubEvents
                mapIsActive={mapIsActive}
                events={page?.events}
                showDates={showDates}
              />
            </div>
          </TabsContent>
          <TabsContent value="sponsors">
            <TabsContainer>
              <LeftContent>
                <Content content={page?.sponsors_content} />
              </LeftContent>
              {page.right_sponsors_content?.length ? (
                <RightContent>
                  <Content
                    content={page.right_sponsors_content}
                    centerButtons
                  />
                </RightContent>
              ) : null}
            </TabsContainer>
          </TabsContent>
          {page?.tabs?.length > 0 &&
            page?.tabs.map((tab, index) => (
              <TabsContent key={index} value={slugify(tab?.tab_title)}>
                <TabsContainer>
                  <LeftContent>
                    <Content content={tab.tab_left_content} />
                  </LeftContent>
                  {tab.tab_right_content.length ? (
                    <RightContent>
                      <Content
                        content={tab.tab_right_content}
                        centerButtons
                      />
                    </RightContent>
                  ) : null}
                </TabsContainer>
              </TabsContent>
            ))}
        </Tabs>
      </Container>
    </div>
  );
}

function TabsContainer({ children, className }) {
  return (
    <div
      className={`grid grid-cols-1 gap-10 w-full lg:grid-cols-4 2xl:grid-cols-3 lg:gap-44 ${className}`}
    >
      {children}
    </div>
  );
}

function LeftContent({ children }) {
  return (
    <div className="col-span-full space-y-6 lg:col-span-2">{children}</div>
  );
}

function RightContent({ children }) {
  return (
    <div className="col-span-1 lg:col-span-2 2xl:col-span-1 max-w-[400px]">
      <div className="p-5 space-y-2 bg-white shadow-lg lg:p-7 rounded-card-radius">
        {children}
      </div>
    </div>
  );
}

function EntryDates({ open, close, lateClose, entriesLink, showEnter }) {
  const [, setTick] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTick((tick) => tick + 1), 1000);
    return () => clearInterval(interval);
  }, []);

  const openCountdown = dayUntil(open);
  const closeCountdown = dayUntil(close);
  const lateCloseCountdown = dayUntil(lateClose);

  const entriesOpen = getTiming(openCountdown, "entries open!");
  const entriesOpenAmount = getAmount(openCountdown);
  const entriesClose = getTiming(closeCountdown, "entries close!");
  const entriesCloseAmount = getAmount(closeCountdown);
  const lateCloseTiming = getTiming(lateCloseCountdown, "late entries close!");
  const lateCloseAmount = getAmount(lateCloseCountdown);

  if (!open && !close && !lateClose) {
    return null;
  }

  if (openCountdown) {
    return (
      <div className="flex items-center pb-5 mb-5 space-x-4 border-b-2 border-brand-red-gum text-button-secondary">
        <h3 className="text-[70px] font-bold leading-none">
          {entriesOpenAmount}
        </h3>
        <div>{entriesOpen}</div>
      </div>
    );
  }

  if (closeCountdown) {
    const closingDateLabel = formatOrdinalDateEnAU(close, { includeTime: true });

    return (
      <div className="flex flex-col items-start pb-5 mb-5 border-b-2">
        <div className="flex items-center space-x-4 text-button-secondary">
          <h3 className="text-[70px] font-bold leading-none">
            {entriesCloseAmount}
          </h3>
          <div>{entriesClose}</div>
        </div>
        {closingDateLabel ? (
          <p className="mt-3 text-sm font-normal leading-snug lg:text-base text-muted-foreground">
            Closing: {closingDateLabel}
          </p>
        ) : null}
        {showEnter && (
          <div className="pt-5 mt-5 w-full border-t-2">
            <Button variant="outline" className="w-full">
              <Link target="_blank" href={entriesLink ?? "#"}>
                Enter Now
              </Link>
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (lateClose && lateCloseCountdown) {
    const lateClosingDateLabel = formatOrdinalDateEnAU(lateClose, {
      includeTime: true,
    });

    return (
      <div className="flex flex-col items-start pb-5 mb-5 border-b-2">
        <p className="pb-3 text-sm font-semibold text-button-secondary">
          Late entries are open — this is your last chance to enter.
        </p>
        <div className="flex items-center space-x-4 text-button-secondary">
          <h3 className="text-[70px] font-bold leading-none">
            {lateCloseAmount}
          </h3>
          <div>{lateCloseTiming}</div>
        </div>
        {lateClosingDateLabel ? (
          <p className="mt-3 text-sm font-normal leading-snug lg:text-base text-muted-foreground">
            Late closing: {lateClosingDateLabel}
          </p>
        ) : null}
        {showEnter && (
          <div className="pt-5 mt-5 w-full border-t-2">
            <Button variant="outline" className="w-full">
              <Link target="_blank" href={entriesLink ?? "#"}>
                Enter Now
              </Link>
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center pb-5 mb-5 space-x-4 border-b-2 border-brand-red-gum text-button-secondary">
      <p className="level-2">Entries Closed</p>
    </div>
  );
}
